import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import Layout from '../components/common/Layout';
import { tenderService, TenderFilters } from '../services/tenderService';
import { callLogService, CreateCallLogData } from '../services/callLogService';
import { Tender, CallLog, CallStatistics } from '../types';
import { useAppData } from '../context/AppDataContext';
import { useAuth } from '../context/AuthContext';
import { FiPhone, FiCalendar, FiMail, FiMapPin, FiMessageSquare, FiArrowLeft, FiX, FiUserPlus, FiEdit2 } from 'react-icons/fi';
import Icon from '../components/common/Icon';
import PriceUpdateHistory from '../components/price/PriceUpdateHistory';
import ClickToCallButton from '../components/calls/ClickToCallButton';
import { formatDate, formatDateTime, getTodayISO, getNowTimeHHMM } from '../utils/dateUtils';
import { useCallOutcomeOptions } from '../hooks/useCallOutcomeOptions';
import CallStatusSelect from '../components/calls/CallStatusSelect';
import PriorCallAlert from '../components/calls/PriorCallAlert';
import { getSettingColorClass } from '../utils/settingColors';
import { usePermissions } from '../hooks/usePermissions';
import { useTenderEditModal } from '../hooks/useTenderEditModal';

type TenderNavState = {
  assignmentId?: number;
  brandId?: number;
  listRestore?: { page: number; perPage: number; filters: TenderFilters };
};

const TenderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { options: callOutcomeOptions, loading: callOutcomeOptionsLoading } = useCallOutcomeOptions();
  const { brands, refreshBrands } = useAppData();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const canEditTender = hasPermission('tenders', 'edit');
  const { openEdit, editModal } = useTenderEditModal(async () => {
    await loadTender();
  });
  const [tender, setTender] = useState<Tender | null>(null);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<number | null>(null);
  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [statistics, setStatistics] = useState<CallStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCallModal, setShowCallModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertError, setConvertError] = useState('');
  const [convertFormData, setConvertFormData] = useState({
    brand_id: undefined as number | undefined,
    status: 'inactive' as string,
    customer_code: '',
    sap_id: '',
    work_order_value: undefined as number | undefined,
    notes: ''
  });
  const [callFormData, setCallFormData] = useState<CreateCallLogData>({
    entity_type: 'tender',
    entity_id: parseInt(id || '0'),
    tender_assignment_id: undefined,
    call_date: getTodayISO(),
    call_time: getNowTimeHHMM(),
    call_type: 'outbound'
  });

  useEffect(() => {
    if (id) {
      const run = async () => {
        await refreshBrands();
        await loadTender();
        loadStatistics();
        setCallFormData((prev) => ({ ...prev, entity_id: parseInt(id) }));
      };
      run();
    }
  }, [id]);

  useEffect(() => {
    if (!id || !tender) return;
    const assignments = tender.assignments || [];
    const sel = selectedAssignmentId
      ? assignments.find((a) => a.id === selectedAssignmentId)
      : assignments[0];
    loadCallLogs(sel?.user_id);
  }, [id, tender?.id, selectedAssignmentId]);

  const loadTender = async (opts?: { preserveAssignment?: boolean }): Promise<Tender | null> => {
    try {
      setLoading(true);
      const data = await tenderService.getById(parseInt(id || '0'));
      setTender(data);
      if (!opts?.preserveAssignment) {
        const stateAssignmentId = (location.state as TenderNavState | null)?.assignmentId;
        const assignments = data.assignments || [];
        if (stateAssignmentId && assignments.some((a) => a.id === stateAssignmentId)) {
          setSelectedAssignmentId(stateAssignmentId);
        } else if (assignments.length > 0) {
          setSelectedAssignmentId(assignments[0].id);
        } else {
          setSelectedAssignmentId(null);
        }
      }
      setError('');
      return data;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load tender');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const loadCallLogs = async (calledByUserId?: number) => {
    try {
      const params = calledByUserId != null ? { called_by: calledByUserId } : undefined;
      const data = await callLogService.getByEntity('tender', parseInt(id || '0'), params);
      setCallLogs(data);
    } catch (err) {
      console.error('Failed to load call logs', err);
    }
  };

  const loadStatistics = async () => {
    try {
      const data = await callLogService.getStatistics('tender', parseInt(id || '0'));
      setStatistics(data);
    } catch (err) {
      console.error('Failed to load statistics', err);
    }
  };

  const handleCallSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await callLogService.create(callFormData);
      setShowCallModal(false);
      setCallFormData({
        entity_type: 'tender',
        entity_id: parseInt(id || '0'),
        tender_assignment_id: selectedAssignmentId ?? undefined,
        call_date: getTodayISO(),
        call_time: getNowTimeHHMM(),
        call_type: 'outbound'
      });
      const latest = await loadTender({ preserveAssignment: true });
      const assignments = latest?.assignments || [];
      const sel = selectedAssignmentId ? assignments.find((a) => a.id === selectedAssignmentId) : assignments[0];
      loadCallLogs(sel?.user_id);
      loadStatistics();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to log call');
    }
  };

  const openCallModal = () => {
    setCallFormData({
      entity_type: 'tender',
      entity_id: parseInt(id || '0'),
      tender_assignment_id: selectedAssignmentId ?? undefined,
      call_date: getTodayISO(),
      call_time: getNowTimeHHMM(),
      call_type: 'outbound'
    });
    setShowCallModal(true);
  };

  const formatCurrency = (value?: number) => {
    if (!value) return '-';
    return `₹${value.toLocaleString('en-IN')}`;
  };

  const getCallOutcomeColor = (outcome?: string) => {
    switch (outcome) {
      case 'interested': return 'bg-green-100 text-green-800';
      case 'not_interested': return 'bg-red-100 text-red-800';
      case 'callback_requested': return 'bg-blue-100 text-blue-800';
      case 'no_response': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-8">Loading...</div>
      </Layout>
    );
  }

  if (!tender) {
    return (
      <Layout>
        <div className="text-center py-8 text-red-600">Tender not found</div>
      </Layout>
    );
  }

  const assignments = tender.assignments || [];
  const selectedAssignment = selectedAssignmentId
    ? assignments.find((a) => a.id === selectedAssignmentId)
    : assignments[0] || null;
  const selectedBrandId = selectedAssignment?.brand_id ?? null;
  const selectedBrandConverted =
    selectedBrandId !== null &&
    ((selectedAssignment as { brand_converted?: boolean })?.brand_converted === true ||
      (tender.converted_brand_ids || []).includes(selectedBrandId));
  const displayStatus = (selectedAssignment?.status ?? tender.status ?? 'PROSPECT') as string;
  const displayNextCallDate = selectedAssignment?.next_call_date ?? tender.next_call_date;
  const displayTotalCalls = selectedAssignment != null ? selectedAssignment.total_calls : (tender.total_calls ?? 0);

  const statusBadgeClass =
    displayStatus === 'INTERSTED' ? 'bg-green-100 text-green-800 border-green-300' :
    displayStatus === 'NOT INTERSTED' ? 'bg-red-100 text-red-800 border-red-300' :
    displayStatus === 'PROSPECT' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
    displayStatus === 'EXISTING OTHERS' ? 'bg-blue-100 text-blue-800 border-blue-300' :
    displayStatus === 'EXISTING INFRASOL' ? 'bg-purple-100 text-purple-800 border-purple-300' :
    displayStatus === 'customer' ? 'bg-purple-100 text-purple-800 border-purple-300' :
    'bg-gray-100 text-gray-800 border-gray-300';

  return (
    <Layout>
      <div className="space-y-3 sm:space-y-4">
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={() => {
              const lr = (location.state as TenderNavState | null)?.listRestore;
              if (lr) {
                navigate('/tenders', { state: { restore: lr } });
              } else {
                navigate('/tenders');
              }
            }}
            className="flex items-center space-x-1 text-indigo-600 hover:text-indigo-800"
          >
            <Icon component={FiArrowLeft} className="w-4 h-4" />
            <span className="text-sm">Back to Tenders</span>
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">
            {error}
          </div>
        )}

        {/* Tender Info Card */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                {tender.awardee_id ? (
                  <Link
                    to={`/tender-awardees/${tender.awardee_id}`}
                    state={{
                      highlightTenderId: tender.id,
                      listRestore: (location.state as TenderNavState | null)?.listRestore,
                    }}
                    className="text-gray-900 hover:text-yellow-700"
                  >
                    {tender.awardee_name || tender.awarder || 'Tender'}
                  </Link>
                ) : (
                  tender.awardee_name || tender.awarder || 'Tender'
                )}
              </h1>
              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                <div className="flex items-center space-x-1">
                  <Icon component={FiCalendar} className="w-4 h-4" />
                  <span>{formatDate(tender.date)}</span>
                </div>
                {tender.awardee_city && (
                  <div className="flex items-center space-x-1">
                    <Icon component={FiMapPin} className="w-4 h-4" />
                    <span>{tender.awardee_city}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3 sm:mt-0 flex flex-col sm:flex-row gap-2 items-start sm:items-center">
              {assignments.length > 1 && (
                <select
                  value={selectedAssignmentId ?? ''}
                  onChange={(e) => setSelectedAssignmentId(e.target.value ? parseInt(e.target.value, 10) : null)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  {assignments.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.brand_name || 'No brand'} – {a.assigned_to_name || a.id}
                    </option>
                  ))}
                </select>
              )}
              <span className={`px-2 py-1 rounded text-xs font-semibold border ${statusBadgeClass}`}>
                {displayStatus}
              </span>
              {(tender.conversion_count ?? 0) > 0 && (
                <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800 border border-green-300">
                  {(tender.conversion_count ?? 0)} conversion{(tender.conversion_count ?? 0) === 1 ? '' : 's'}
                </span>
              )}
              {selectedBrandConverted ? (
                <span className="px-2 py-1 rounded text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-300">
                  This brand converted
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setConvertError('');
                    setConvertFormData({
                      brand_id: selectedBrandId ?? undefined,
                      status: 'inactive',
                      customer_code: '',
                      sap_id: '',
                      work_order_value: tender.work_order_value,
                      notes: ''
                    });
                    setShowConvertModal(true);
                  }}
                  className="px-3 py-1.5 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium flex items-center gap-1.5 font-bold"
                >
                  <Icon component={FiUserPlus} className="w-4 h-4" />
                  Convert to Customer
                </button>
              )}
            </div>
            {tender.work_order_value != null && tender.work_order_value !== undefined && (
              <div className="mt-3 sm:mt-0 sm:ml-4">
                <div className="text-lg font-bold text-indigo-600">
                  {formatCurrency(tender.work_order_value)}
                </div>
                <div className="text-xs text-gray-500">Work Order Value</div>
              </div>
            )}
          </div>

          {tender.conversions && tender.conversions.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-gray-500">Customer records:</span>
              {tender.conversions.map((c) => (
                <Link
                  key={`${c.customer_id}-${c.brand_id ?? 'x'}`}
                  to={`/customers/${c.customer_id}`}
                  className="text-yellow-700 hover:text-yellow-900 font-medium underline"
                >
                  {c.customer_code || `ID ${c.customer_id}`}
                  {c.brand_id != null ? ` (brand #${c.brand_id})` : ''}
                </Link>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 text-sm">
            {tender.awardee_phone && (
              <div>
                <span className="text-gray-500 flex items-center space-x-1">
                  <Icon component={FiPhone} className="w-4 h-4" />
                  <span>Phone:</span>
                </span>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-gray-900">{tender.awardee_phone}</p>
                  <ClickToCallButton
                    entityType="tender"
                    entityId={tender.id}
                    entityName={tender.awardee_name || tender.awarder || 'Tender'}
                    phoneNumber={tender.awardee_phone}
                  />
                </div>
              </div>
            )}
            {tender.awardee_email && (
              <div>
                <span className="text-gray-500 flex items-center space-x-1">
                  <Icon component={FiMail} className="w-4 h-4" />
                  <span>Email:</span>
                </span>
                <p className="text-gray-900">{tender.awardee_email}</p>
              </div>
            )}
            {tender.industry && (
              <div>
                <span className="text-gray-500">Industry:</span>
                <p className="text-gray-900">{tender.industry}</p>
              </div>
            )}
            {tender.source && (
              <div>
                <span className="text-gray-500">Source:</span>
                <p className="text-gray-900">{tender.source}</p>
              </div>
            )}
            {tender.city && (
              <div>
                <span className="text-gray-500">City:</span>
                <p className="text-gray-900">{tender.city}</p>
              </div>
            )}
            {tender.completion_months && (
              <div>
                <span className="text-gray-500">Completion Months:</span>
                <p className="text-gray-900">{tender.completion_months}</p>
              </div>
            )}
            {tender.product_req && (
              <div>
                <span className="text-gray-500">Product Req:</span>
                <p className="text-gray-900">{tender.product_req}</p>
              </div>
            )}
            {tender.qty_req && (
              <div>
                <span className="text-gray-500">Qty Req:</span>
                <p className="text-gray-900">{tender.qty_req}</p>
              </div>
            )}
            {tender.price && (
              <div>
                <span className="text-gray-500">Price:</span>
                <p className="text-gray-900">{formatCurrency(tender.price)}</p>
              </div>
            )}
            {tender.brands_in_use && (
              <div>
                <span className="text-gray-500">Brands in Use:</span>
                <p className="text-gray-900">{tender.brands_in_use}</p>
              </div>
            )}
          </div>
          {tender.scope_of_work && (
            <div className="mt-4">
              <span className="text-gray-500 text-sm">Scope of Work:</span>
              <p className="text-gray-900 mt-1">{tender.scope_of_work}</p>
            </div>
          )}
          {tender.update_status && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <span className="text-sm font-semibold text-blue-900">Update Status:</span>
              <p className="text-sm text-blue-800 mt-1">{tender.update_status}</p>
            </div>
          )}
        </div>
        {tender.awardee_id != null && (
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
            <h2 className="text-lg font-bold text-gray-900 mb-1">Tenders History</h2>
            <p className="text-sm text-gray-600 mb-4">
              {(tender.tender_history?.length ?? 0) === 0
                ? `No tenders on record for ${tender.awardee_name || 'this awardee'}.`
                : `${tender.tender_history_count ?? tender.tender_history?.length ?? 0} tender${
                    (tender.tender_history_count ?? tender.tender_history?.length ?? 0) === 1 ? '' : 's'
                  } for ${tender.awardee_name || 'this awardee'}`}
              {tender.awardee_id && (
                <>
                  {' '}
                  <Link
                    to={`/tender-awardees/${tender.awardee_id}`}
                    state={{
                      highlightTenderId: parseInt(id || '0', 10),
                      listRestore: (location.state as TenderNavState | null)?.listRestore,
                    }}
                    className="text-yellow-700 hover:underline"
                  >
                    View awardee profile
                  </Link>
                </>
              )}
            </p>
            {(tender.tender_history?.length ?? 0) > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Date</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Awarder</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">City</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Status</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Brand</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Work order</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-700"> </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {tender.tender_history!.map((h) => {
                      const isCurrent = h.id === parseInt(id || '0', 10);
                      return (
                      <tr
                        key={h.id}
                        className={isCurrent ? 'bg-yellow-50 hover:bg-yellow-100' : 'hover:bg-gray-50'}
                      >
                        <td className="px-3 py-2 whitespace-nowrap">{formatDate(h.date)}</td>
                        <td className="px-3 py-2 max-w-[200px] truncate" title={h.awarder || ''}>
                          {h.awarder || '—'}
                        </td>
                        <td className="px-3 py-2">{h.city || h.awardee_city || '—'}</td>
                        <td className="px-3 py-2">
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                            {h.status || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2">{h.brand_name || '—'}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {h.work_order_value != null ? formatCurrency(h.work_order_value) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-2">
                            {isCurrent && (
                              <span className="text-xs font-semibold text-yellow-800">Current</span>
                            )}
                            {!isCurrent && (
                              <Link
                                to={`/tenders/${h.id}`}
                                state={{
                                  assignmentId: h.assignment_id,
                                  brandId: h.brand_id ?? undefined,
                                  listRestore: (location.state as TenderNavState | null)?.listRestore,
                                }}
                                className="text-yellow-700 hover:underline font-medium"
                              >
                                View
                              </Link>
                            )}
                            {canEditTender && (
                              <button
                                type="button"
                                onClick={() => openEdit(h)}
                                className="text-gray-600 hover:text-yellow-700 inline-flex items-center gap-0.5"
                                title="Edit tender"
                                aria-label="Edit tender"
                              >
                                <Icon component={FiEdit2} className="w-3.5 h-3.5" />
                                <span className="text-xs font-medium">Edit</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Call Statistics */}
        {statistics && (
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
            <h2 className="text-lg font-bold text-gray-900 mb-3">Call Statistics</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{displayTotalCalls}</div>
                <div className="text-xs text-gray-600 mt-1">Total Calls (this assignment)</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{statistics.answered_calls || 0}</div>
                <div className="text-xs text-gray-600 mt-1">Answered</div>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{statistics.no_answer_calls || 0}</div>
                <div className="text-xs text-gray-600 mt-1">No Answer</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{statistics.interested_calls || 0}</div>
                <div className="text-xs text-gray-600 mt-1">Interested</div>
              </div>
            </div>
            {displayNextCallDate && (
              <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Icon component={FiCalendar} className="w-5 h-5 text-orange-600" />
                  <div>
                    <div className="text-sm font-semibold text-orange-900">Next Call Scheduled</div>
                    <div className="text-sm text-orange-700">{formatDateTime(displayNextCallDate)}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quick Call Log Button */}
        <div className="flex justify-end">
          <button
            onClick={openCallModal}
            className="flex items-center space-x-2 bg-yellow-500 text-black-950 px-4 py-2 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold"
          >
            <Icon component={FiPhone} className="w-4 h-4" />
            <span>Log New Call</span>
          </button>
        </div>

        {/* Call History Timeline */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Call History</h2>
          {callLogs.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-500">No call logs yet</div>
          ) : (
            <div className="space-y-4">
              {callLogs.map((log) => (
                <div key={log.id} className="border-l-4 border-indigo-500 pl-4 py-2">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2">
                    <div className="flex items-center space-x-2">
                      <Icon component={FiPhone} className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-semibold text-gray-900">
                        {formatDateTime(log.call_date, log.call_time)}
                      </span>
                      {log.called_by_name && (
                        <span className="text-xs text-gray-500">by {log.called_by_name}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2 sm:mt-0">
                      {log.call_status && (
                        <span className={`px-2 py-1 rounded text-xs font-semibold border ${getSettingColorClass(log.call_status_color_code)}`}>
                          {log.call_status}
                        </span>
                      )}
                      {log.call_outcome && (
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getCallOutcomeColor(log.call_outcome)}`}>
                          {log.call_outcome}
                        </span>
                      )}
                    </div>
                  </div>
                  {log.notes && (
                    <div className="text-sm text-gray-700 mt-2 flex items-start space-x-2">
                      <Icon component={FiMessageSquare} className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <p className="flex-1">{log.notes}</p>
                    </div>
                  )}
                  {log.next_call_date && (
                    <div className="mt-2 text-xs text-indigo-600 flex items-center space-x-1">
                      <Icon component={FiCalendar} className="w-3 h-3" />
                      <span>Next call: {formatDateTime(log.next_call_date)}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Price Update History */}
        {tender && (
          <PriceUpdateHistory
            entityType="tender"
            entityId={tender.id}
            entityName={tender.awardee_name || tender.awarder || 'Tender'}
          />
        )}

        {/* Call Log Modal */}
        {showCallModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-3 sm:p-4">
            <div className="relative top-2 sm:top-10 mx-auto p-4 sm:p-5 border w-full max-w-md shadow-xl rounded-lg bg-white max-h-[95vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">Log New Call</h3>
                <button
                  type="button"
                  onClick={() => setShowCallModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  aria-label="Close"
                >
                  <Icon component={FiX} className="w-5 h-5" />
                </button>
              </div>
              {tender && <PriorCallAlert entityType="tender" entityId={tender.id} />}
              <form onSubmit={handleCallSubmit} className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Date *</label>
                    <input
                      type="date"
                      required
                      value={callFormData.call_date}
                      onChange={(e) => setCallFormData({ ...callFormData, call_date: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Time</label>
                    <input
                      type="time"
                      value={callFormData.call_time}
                      onChange={(e) => setCallFormData({ ...callFormData, call_time: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Status</label>
                  <CallStatusSelect
                    value={callFormData.call_status_id}
                    onChange={(call_status_id) => setCallFormData({ ...callFormData, call_status_id })}
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Outcome</label>
                  <select
                    value={callFormData.call_outcome || ''}
                    onChange={(e) => setCallFormData({ ...callFormData, call_outcome: e.target.value || undefined })}
                    disabled={callOutcomeOptionsLoading}
                    className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm disabled:opacity-60"
                  >
                    <option value="">{callOutcomeOptionsLoading ? 'Loading…' : 'Select Outcome'}</option>
                    {callOutcomeOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={callFormData.notes || ''}
                    onChange={(e) => setCallFormData({ ...callFormData, notes: e.target.value })}
                    className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    rows={4}
                    placeholder="Enter call notes..."
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Next Call Date</label>
                  <input
                    type="datetime-local"
                    value={callFormData.next_call_date ? (() => {
                      // Convert ISO string (UTC) to local datetime-local format (YYYY-MM-DDTHH:mm)
                      const date = new Date(callFormData.next_call_date);
                      // Get local time components
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const hours = String(date.getHours()).padStart(2, '0');
                      const minutes = String(date.getMinutes()).padStart(2, '0');
                      return `${year}-${month}-${day}T${hours}:${minutes}`;
                    })() : ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        // datetime-local value is in local time, convert to ISO (UTC) for storage
                        // Create date from local datetime string (browser interprets as local time)
                        const localDateTime = e.target.value; // Format: YYYY-MM-DDTHH:mm (local time)
                        const date = new Date(localDateTime);
                        // Convert to ISO string (UTC) for backend storage
                        setCallFormData({ ...callFormData, next_call_date: date.toISOString() });
                      } else {
                        setCallFormData({ ...callFormData, next_call_date: undefined });
                      }
                    }}
                    className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                  />
                </div>
                {callFormData.next_call_date && (
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Next Call Notes</label>
                    <textarea
                      value={callFormData.next_call_notes || ''}
                      onChange={(e) => setCallFormData({ ...callFormData, next_call_notes: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                      rows={2}
                      placeholder="Notes for next call..."
                    />
                  </div>
                )}
                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-3 sm:pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCallModal(false)}
                    className="px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1.5 sm:px-4 sm:py-2 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold"
                  >
                    Log Call
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showConvertModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-3 sm:p-4">
            <div className="relative top-2 sm:top-10 mx-auto p-4 sm:p-5 border w-full max-w-2xl shadow-xl rounded-lg bg-white max-h-[95vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">Convert tender to customer</h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowConvertModal(false);
                    setConvertError('');
                  }}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  aria-label="Close"
                >
                  <Icon component={FiX} className="w-5 h-5" />
                </button>
              </div>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  setConvertError('');
                  try {
                    const result = await tenderService.convertToCustomer(parseInt(id || '0'), convertFormData);
                    setShowConvertModal(false);
                    await loadTender({ preserveAssignment: true });
                    alert(`Converted successfully. Customer ID: ${result.customer_id}.`);
                  } catch (err: any) {
                    const data = err.response?.data;
                    if (data?.error === 'duplicate_awardee_conversion') {
                      const code = data.existing_customer_code ? ` (${data.existing_customer_code})` : '';
                      const proceed = window.confirm(
                        `${data.message || 'Another tender for this awardee was already converted for this brand.'}` +
                          `\nExisting customer${code} from tender #${data.existing_tender_id}. Create another customer anyway?`
                      );
                      if (proceed) {
                        try {
                          const result = await tenderService.convertToCustomer(parseInt(id || '0'), {
                            ...convertFormData,
                            confirm_duplicate_awardee: true,
                          });
                          setShowConvertModal(false);
                          await loadTender({ preserveAssignment: true });
                          alert(`Converted successfully. Customer ID: ${result.customer_id}.`);
                          return;
                        } catch (retryErr: any) {
                          setConvertError(retryErr.response?.data?.message || 'Failed to convert tender to customer');
                          return;
                        }
                      }
                      setConvertError(data.message || 'Conversion cancelled');
                      return;
                    }
                    setConvertError(data?.message || 'Failed to convert tender to customer');
                  }
                }}
                className="space-y-3 sm:space-y-4"
              >
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>Tender:</strong> {tender.awardee_name || tender.awarder}
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Brand *</label>
                    <select
                      required
                      value={convertFormData.brand_id || ''}
                      onChange={(e) =>
                        setConvertFormData({ ...convertFormData, brand_id: e.target.value ? parseInt(e.target.value, 10) : undefined })
                      }
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    >
                      <option value="">Select Brand</option>
                      {brands.map((brand) => (
                        <option key={brand.id} value={brand.id}>
                          {brand.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Status *</label>
                    <select
                      required
                      value={convertFormData.status}
                      onChange={(e) => setConvertFormData({ ...convertFormData, status: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    >
                      <option value="inactive">Inactive</option>
                      <option value="active">Active</option>
                      <option value="dormant">Dormant</option>
                      <option value="lost">Lost</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Customer Code</label>
                    <input
                      type="text"
                      value={convertFormData.customer_code}
                      onChange={(e) => setConvertFormData({ ...convertFormData, customer_code: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">SAP ID</label>
                    <input
                      type="text"
                      value={convertFormData.sap_id}
                      onChange={(e) => setConvertFormData({ ...convertFormData, sap_id: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Work Order Value</label>
                    <input
                      type="number"
                      step="0.01"
                      value={convertFormData.work_order_value ?? ''}
                      onChange={(e) =>
                        setConvertFormData({
                          ...convertFormData,
                          work_order_value: e.target.value ? parseFloat(e.target.value) : undefined
                        })
                      }
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Conversion Notes</label>
                  <textarea
                    value={convertFormData.notes}
                    onChange={(e) => setConvertFormData({ ...convertFormData, notes: e.target.value })}
                    className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    rows={3}
                  />
                </div>
                {convertError && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">{convertError}</div>
                )}
                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-3 sm:pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowConvertModal(false);
                      setConvertError('');
                    }}
                    className="px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1.5 sm:px-4 sm:py-2 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold"
                  >
                    Convert to Customer
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {editModal}
      </div>
    </Layout>
  );
};

export default TenderDetail;

